import React, { Component } from 'react';
import { connect } from 'react-redux';

import { YCaption, YMap, YMapPoint, YClusterPoint } from '../../common/YMap';
import cn from 'classnames';
import styles from '../Map/Map.scss';
import { fitBounds } from 'google-map-react/utils';
import {
  regionsLabels,
  microregionsLabels,
  ZOOM
} from 'constants';
import SuperCluster from 'supercluster';
import { loadPoint } from '../../../actions/objects';
import YPlaceInfo from '../../common/YMap/YPlaceInfo';

const size = {
  width: 800,
  height: 500
};

const clusterOptions = {
  radius: 68,
  maxZoom: 14,
  minPoints: 10
};

class MainMap extends Component {
  constructor(props) {
    super(props);

    const mapProps = this.fitBounds(props.data);
    const data = this.transformData(props.data);

    this.state = {
      mapProps,
      data,
      key: 1
    };
  }

  componentWillReceiveProps(nextProps) {
    const { data: nextData } = nextProps;
    const { data } = this.props;
    if (nextData !== data) {
      const mapProps = this.fitBounds(nextData);
      const data = this.transformData(nextData);
      this.setState(state => ({ data, mapProps, info: null, key: state.key + 1 }));

      this.map.setCenter([mapProps.center.lat, mapProps.center.lng], mapProps.zoom);
    }
  }

  transformData(data) {
    return data.map((place) => ({ ...place, geometry: { coordinates: [place.longitude, place.latitude] } }));
  }

  map = null;

  fitBounds(data) {
    let maxLat = 0,
      minLat = 1000,
      maxLng = 0,
      minLng = 1000;

    if (data.length > 1) {
      data.forEach((item, i) => {
        const { latitude, longitude } = item;
        if (latitude > maxLat) maxLat = latitude;
        if (latitude < minLat) minLat = latitude;
        if (longitude > maxLng) maxLng = longitude;
        if (longitude < minLng) minLng = longitude;
      });
    } else {
      const { latitude, longitude } = data[0];
      maxLat = Number(latitude) + 0.005;
      minLat = Number(latitude) - 0.005;
      maxLng = Number(longitude) + 0.005;
      minLng = Number(longitude) - 0.005;
    }

    const bounds = {
      nw: {
        lat: maxLat,
        lng: minLng
      },
      se: {
        lat: minLat,
        lng: maxLng
      }
    };

    return fitBounds(bounds, size);
  }

  getClusters() {
    if (!(this.state.mapProps.bounds || this.state.mapProps.newBounds)) {
      return [];
    }

    const { data, mapProps } = this.state;
    const { zoom, bounds, newBounds } = mapProps;
    const { nw, se } = (bounds || newBounds);

    const index = new SuperCluster(clusterOptions);
    index.load(data);

    return index.getClusters([nw.lng, se.lat, se.lng, nw.lat], zoom);
  }

  handleMapLoad = (ymaps) => {
    const myMap = this.map;

    myMap.events.add('boundschange', (event) => {
      const points = event.get('newBounds').map(([latitude, longitude]) => ({ latitude, longitude }));
      const mapProps = this.fitBounds(points);

      this.setState({
        mapProps
      });
    });
  }

  handleClusterClick = (cluster) => {
    const index = new SuperCluster(clusterOptions);
    index.load(this.state.data);

    const clusterZoom = index.getClusterExpansionZoom(cluster.id);
    const { geometry: { coordinates: [longitude, latitude] } } = cluster;

    this.map.setCenter([latitude, longitude], clusterZoom + 1);
    this.setState({
      info: null
    });
  }

  renderCaptions() {
    const { microregions } = this.props;
    const { mapProps: { zoom } } = this.state;

    const captions = zoom >= ZOOM
      ? microregionsLabels.filter((_, i) => !microregions || microregions.includes((i + 1).toString()))
      : microregions ? [] : regionsLabels;

    return captions.map(
      ({ name, lat, lng }) => <YCaption name={name} lat={lat} lng={lng} key={`${name}-${lat}-${lng}`} />
    );
  }

  handleShowInfo = (item, info) => {
    const { id, longitude, latitude } = item;
    const { points } = this.props;

    this.setState({
      info: { ...info, id }
    });

    this.map.setCenter([latitude, longitude]);


    if (points && !points[id]) {
      const { dispatch } = this.props;
      dispatch(loadPoint(id));
    }
  };

  handleCloseInfo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ info: null });
  };

  render() {
    const { className, points, infoType } = this.props;
    const { mapProps: { center, zoom, newBounds }, info } = this.state;
    const clusters = this.getClusters();

    const places = clusters.map((item) => {
      if (item.geometry.type) {
        const { properties: { point_count: pointCount }, geometry: { coordinates: [longitude, latitude] } } = item;
        return (
          <YClusterPoint
            name={pointCount}
            pointCount={pointCount}
            lat={latitude}
            lng={longitude}
            onClick={this.handleClusterClick.bind(undefined, item)}
            key={`${pointCount}-${latitude}-${longitude}`}
          />
        );
      }

      const { longitude, latitude, id } = item;

      let contexuality;
      const isActive = false;

      if (Number(item.count_flats) === 0) {
        contexuality = 'disabled';
      } else if (item.construction_finished) {
        contexuality = isActive ? 'active' : 'success';
      } else {
        contexuality = isActive ? 'warningActive' : 'warningSuccess';
      }

      if (item.status == 3) {
        contexuality = 'almostFinished';
      }

      return (
        <YMapPoint
          key={`${id}-${latitude}-${longitude}`}
          lat={+latitude}
          lng={+longitude}
          onClick={this.handleShowInfo.bind(this, item)}
          contexuality={contexuality}
          isActive={this.state.info && this.state.info.id === id}
        />
      );
    });

    return (
      <div className={cn(styles.Map, className)}>
        <YMap
          key={this.state.key}
          defaultState={{
            center: [center.lat, center.lng],
            zoom
          }}
          defaultOptions={{
            restrictMapArea: [
              [newBounds.sw.lat - 1.5, newBounds.sw.lng - 1.5],
              [newBounds.ne.lat + 1.5, newBounds.ne.lng + 1.5]
            ]
          }}
          instanceRef={ref => this.map = ref}
          onLoad={this.handleMapLoad}
        >
          {info && points && points[info.id] && (
            <div className={styles.MapInfo}>
              <YPlaceInfo onClose={this.handleCloseInfo} infoType={infoType} point={points[info.id]} />
            </div>
          )}
          {this.renderCaptions()}
          {places}
        </YMap>
      </div>
    );
  }
}

export default connect(state => ({ microregions: state.search.request.subregion }))(MainMap);



// WEBPACK FOOTER //
// ./src/components/Buildings/YMap/index.js